/**
 * ChannelsChoice template.
 */
import ChannelView from '@shared/types/enums/channelView';
import ChannelsChoiceList from '../sections/channelChoice/channelsChoiceList';
import ContentContainer from '@components/atoms/container/contentContainer';
import ChannelsChoiceDescription from '../sections/channelChoice/channelsChoiceDescription';
import Focus from '@shared/types/enums/focus';
import Screen from '@shared/utils/screen';
import Storage from '../../../shared/utils/storage';
import goToChannelMain from '@shared/actions/channels/goToChannelMain';
import goToClassicChannel from '@shared/actions/channels/goToClassicChannel';
import useKeyboard from '@shared/components/utils/hooks/useKeyboard';
import { setFocus } from '@shared/store/slices/focus.slice';
import { useDispatch } from 'react-redux';
import { useCallback, useMemo } from 'react';

/**
 * Displays different choice views available for the channels.
 */
export default function ChannelsChoiceTemplate() {
  const { callbacks } = useChannelsChoice();
  return (
    <ContentContainer
      {...callbacks.container}
      style={{ paddingRight: `${100 * Screen.widthRatio}px` }}
    >
      <ChannelsChoiceDescription />
      <ChannelsChoiceList {...callbacks.choiceContainer} />
    </ContentContainer>
  );
}

/**
 * Hooks for the channels choice template. Returns the props required for its children.
 */
function useChannelsChoice() {
  // ------------------------------------------------------------
  // Variables
  // ------------------------------------------------------------

  // Dispatch
  const dispatch = useDispatch();

  // ------------------------------------------------------------
  // Event handlers
  // ------------------------------------------------------------

  /**
   * Handles the mouse enter event.
   */
  const onMouseEnter = useCallback(() => {
    dispatch(setFocus(Focus.CHANNELS_CHOICE));
  }, []);

  /**
   * Selects a view based on the index.
   */
  const onSelectView = useCallback((index: number) => {
    Storage.set('userChannelViewSelection', `${index}`);
    if (index === ChannelView.Classic) {
      goToClassicChannel();
    } else {
      goToChannelMain();
    }
  }, []);

  // ------------------------------------------------------------
  // Keyboard hooks
  // ------------------------------------------------------------

  useKeyboard({
    canBeFocused: false,
    keyDownHandler: (key) => {
      switch (key) {
        case 'RIGHT':
        case 'SELECT':
          dispatch(setFocus(Focus.CHANNELS_CHOICE));
          break;
      }
    },
  });

  // ------------------------------------------------------------
  // Memoized values
  // ------------------------------------------------------------

  return useMemo(() => {
    return {
      callbacks: {
        container: {
          onMouseEnter,
        },
        choiceContainer: {
          onSelectView,
        },
      },
    };
  }, [onMouseEnter, onSelectView]);
}
